import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "@nexirt/ui";
import { useTurnstileToken } from "../lib/turnstile";

type State = "idle" | "submitting" | "ok" | "err";

/**
 * Footer newsletter capture — Rev 27 Loop I.
 *
 * Compact single-line form. On submit:
 *   - POSTs to ${VITE_API_BASE}/marketing/subscribe with the email,
 *     source="footer", honeypot, and a Turnstile token (if available).
 *   - Swaps the form for an aria-live status pill so screen readers
 *     announce the confirmation prompt.
 *
 * Submit chip uses the same purple→teal gradient + hover lift + glow
 * as SocialIconButton so the footer's brand column reads as one
 * coherent visual chord.
 *
 * Honeypot: a visually-hidden `company_website` input. Real users tab
 * past it (tabIndex=-1, aria-hidden); bots that DOM-scrape fill every
 * field. Backend short-circuits on non-empty.
 */

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.nexirt.com";

export default function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [state, setState] = useState<State>("idle");
  const [msg, setMsg] = useState<string>("");
  const turnstileToken = useTurnstileToken();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    try {
      const res = await fetch(`${API_BASE}/marketing/subscribe`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          source: "footer",
          company_website: hp,
          token: turnstileToken ?? undefined,
        }),
      });
      if (res.ok || res.status === 202) {
        setState("ok");
        setMsg("Check your inbox — confirmation sent.");
      } else {
        setState("err");
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setMsg(
          body?.error === "bad email"
            ? "That email looks off. Try again."
            : "Something went wrong. Try again in a moment.",
        );
      }
    } catch {
      setState("err");
      setMsg("Network hiccup. Try again in a moment.");
    }
  }

  if (state === "ok") {
    return (
      <div
        data-footer-newsletter-status="ok"
        aria-live="polite"
        className="mt-5 inline-flex items-center gap-2 rounded-nx2 border border-nx-accent/40 bg-nx-surface px-3 py-2 text-xs text-nx-text"
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-nx-accent-2 shadow-[0_0_8px_var(--nx-accent-glow)]"
        />
        {msg}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-footer-newsletter
      className="mt-5"
      aria-label="Subscribe to NEXIRT dispatch"
    >
      <label
        htmlFor="footer-newsletter-email"
        className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint"
      >
        Dispatch · 1-2 per month
      </label>
      <div className="flex items-stretch gap-1.5">
        <input
          id="footer-newsletter-email"
          type="email"
          required
          placeholder="you@domain.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          spellCheck={false}
          disabled={state === "submitting"}
          aria-invalid={state === "err"}
          aria-describedby={state === "err" ? "footer-newsletter-msg" : "footer-newsletter-msg-idle"}
          className={cn(
            "min-w-0 flex-1 rounded-nx2 border bg-nx-surface px-3 py-2 text-sm text-nx-text placeholder:text-nx-text-faint",
            "border-nx-line focus:border-nx-accent focus:outline-none focus:ring-2 focus:ring-nx-accent/30",
            "transition-[border-color,box-shadow] duration-[180ms] ease-nx",
            state === "err" && "border-nx-danger/60",
          )}
        />
        {/* Honeypot — visually hidden, kept reachable for bots that
            parse the DOM. Real users tabbing through skip it via
            tabIndex={-1}. */}
        <input
          type="text"
          name="company_website"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          style={{ position: "absolute" }}
        />
        <button
          type="submit"
          disabled={state === "submitting" || email.length === 0}
          aria-label="Subscribe"
          className={cn(
            "group relative inline-flex h-[38px] w-[42px] items-center justify-center overflow-hidden rounded-nx2",
            "bg-[linear-gradient(135deg,var(--nx-accent)_0%,var(--nx-accent-2)_100%)] text-white",
            "transition-[transform,box-shadow] duration-[220ms] ease-nx",
            "hover:-translate-y-[1px] hover:shadow-[0_10px_28px_-6px_var(--nx-accent-glow)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nx-accent focus-visible:ring-offset-2 focus-visible:ring-offset-nx-bg",
            "disabled:cursor-not-allowed disabled:opacity-60",
            "motion-reduce:transform-none motion-reduce:transition-[box-shadow]",
          )}
        >
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      <p
        id={state === "err" ? "footer-newsletter-msg" : "footer-newsletter-msg-idle"}
        aria-live="polite"
        className={cn(
          "mt-1.5 min-h-[1.1em] text-[11px]",
          state === "err" ? "text-nx-danger" : "text-nx-text-faint",
        )}
      >
        {state === "err" ? msg : "No spam. Unsubscribe anytime."}
      </p>
    </form>
  );
}
